import { useState } from "preact/hooks";
import { RocketLaunchIcon } from "@heroicons/react/24/outline";
import { api } from "../../lib/api-client";
import { useConfirm } from "../../lib/use-confirm";
import { useDeployHook } from "../../lib/use-deploy-hook";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Field,
  Input,
  SectionHeading,
} from "../ui";
import type { ProjectDto } from "@shared/types";

type Props = {
  project: ProjectDto;
  onProjectUpdate: (next: ProjectDto) => void;
};

export function DeployHookSection({ project, onProjectUpdate }: Props) {
  const [hookUrlInput, setHookUrlInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const { state: deployState, trigger: onTrigger } = useDeployHook(project);
  const { confirm, dialog: confirmDialog } = useConfirm();

  async function onSave(e: Event) {
    e.preventDefault();
    const trimmed = hookUrlInput.trim();
    // Empty + already configured = explicit clear. Confirm to avoid losing the
    // saved hook by accident (we can't echo it back to recover).
    if (trimmed === "" && project.deployHookConfigured) {
      const ok = await confirm({
        title: "保存済みのデプロイフック URL をクリアしますか？",
        description:
          "クリア後はこのプロジェクトからデプロイをトリガーできません。再設定時は URL を貼り直す必要があります。",
        confirmLabel: "クリアする",
        tone: "danger",
      });
      if (!ok) return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const next = await api.updateProject(project.id, {
        deployHookUrl: trimmed === "" ? null : trimmed,
      });
      onProjectUpdate(next);
      setHookUrlInput("");
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const isClearAction =
    hookUrlInput.trim() === "" && project.deployHookConfigured;

  return (
    <div class="space-y-4">
      {confirmDialog}
      <SectionHeading level={3} title="デプロイフック" />
      <Card>
        <CardBody class="space-y-4">
          <form onSubmit={onSave} class="space-y-3">
            <Field
              label="フロントエンドの Deploy Hook URL"
              hint={
                project.deployHookUrlMasked ? (
                  <>
                    保存済み:{" "}
                    <span class="font-mono text-fg-muted">
                      {project.deployHookUrlMasked}
                    </span>
                  </>
                ) : undefined
              }
            >
              <Input
                type="url"
                value={hookUrlInput}
                onInput={(e) =>
                  setHookUrlInput((e.target as HTMLInputElement).value)
                }
                placeholder={
                  project.deployHookConfigured
                    ? "設定済み (置き換える場合のみ入力)"
                    : "https://api.cloudflare.com/.../deploy_hooks/..."
                }
                autoComplete="off"
              />
            </Field>
            <div class="flex flex-wrap items-center gap-2">
              <Button
                type="submit"
                variant="secondary"
                loading={saving}
                disabled={
                  saving ||
                  (hookUrlInput.trim() === "" && !project.deployHookConfigured)
                }
              >
                {saving
                  ? "保存中…"
                  : isClearAction
                    ? "URL をクリア"
                    : "URL を保存"}
              </Button>
              <Button
                type="button"
                variant="primary"
                iconLeft={<RocketLaunchIcon class="h-4 w-4" />}
                loading={deployState.kind === "running"}
                onClick={onTrigger}
                disabled={
                  !project.deployHookConfigured ||
                  deployState.kind === "running"
                }
              >
                {deployState.kind === "running"
                  ? "実行中…"
                  : "デプロイを実行"}
              </Button>
            </div>
            {saveError && (
              <Alert tone="danger">
                {saveError}
              </Alert>
            )}
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
